import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import TemplateGallery from './components/TemplateGallery';
import AIContentPanel from './components/AIContentPanel';
import AdvancedCanvas from './components/AdvancedCanvas';
import InteractivePreview from './components/InteractivePreview';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import CollaborationTools from './components/CollaborationTools';
import ExportOptions from './components/ExportOptions';
import Icon from '../../components/AppIcon';
import { AIContentGenerator } from '../../services/aiService';

const EnhancedPressKitBuilder = () => {
  const [activeTab, setActiveTab] = useState('design');
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [pressKitElements, setPressKitElements] = useState([]);
  const [aiGeneratedContent, setAiGeneratedContent] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [analytics, setAnalytics] = useState({
    views: 1247,
    downloads: 89,
    engagement: 73,
    topSections: ['author-bio', 'book-cover', 'media-gallery']
  });
  const [collaborators, setCollaborators] = useState([]);
  const [version, setVersion] = useState('1.0');

  // Enhanced press kit data with industry-specific metadata
  const [pressKitData, setPressKitData] = useState({
    title: 'The Quantum Chronicles Press Kit',
    subtitle: 'Revolutionary Science Fiction by Dr. Sarah Chen',
    industry: 'literary-fiction',
    genre: 'science-fiction',
    targetAudience: 'adult-readers',
    keyThemes: ['quantum physics', 'time travel', 'human consciousness'],
    publishDate: '2024-03-15',
    publisher: 'Stellar Publications',
    isbn: '978-0-123456-78-9',
    status: 'draft',
    seoOptimized: true,
    mobileResponsive: true,
    lastModified: new Date()
  });

  // AI-powered content generation
  const handleAIContentGeneration = async (contentType, params) => {
    setIsGenerating(true);
    try {
      let generatedContent = {};

      switch (contentType) {
        case 'press-release':
          generatedContent = await AIContentGenerator?.generatePressRelease({
            topic: pressKitData?.title,
            company: pressKitData?.publisher,
            industry: 'publishing',
            keyPoints: pressKitData?.keyThemes,
            tone: 'professional'
          });
          break;

        case 'author-bio':
          generatedContent = await AIContentGenerator?.generateBlogPost({
            topic: `Author biography for ${params?.authorName}`,
            targetAudience: pressKitData?.targetAudience,
            wordCount: 300,
            keywords: ['author', 'writer', pressKitData?.genre]
          });
          break;

        case 'social-media':
          generatedContent = await AIContentGenerator?.generateSocialMediaContent({
            platform: params?.platform || 'twitter',
            topic: pressKitData?.title,
            tone: 'engaging',
            hashtags: [`#${pressKitData?.genre}`, '#newrelease', '#booklaunch']
          });
          break;

        case 'story-angles':
          generatedContent = await AIContentGenerator?.generateStoryAngles({
            topic: pressKitData?.title,
            industry: 'publishing',
            newsHook: `New ${pressKitData?.genre} novel explores ${pressKitData?.keyThemes?.join(', ')}`,
            targetMedia: ['literary-magazines', 'sci-fi-blogs', 'mainstream-media']
          });
          break;

        default:
          throw new Error(`Unsupported content type: ${contentType}`);
      }

      setAiGeneratedContent((prev) => ({
        ...prev,
        [contentType]: generatedContent
      }));

      // Auto-add generated content as elements
      if (generatedContent) {
        const newElement = createElementFromAIContent(contentType, generatedContent);
        setPressKitElements((prev) => [...prev, newElement]);
      }

    } catch (error) {
      console.error(`Error generating ${contentType}:`, error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Convert AI-generated content to press kit elements
  const createElementFromAIContent = (contentType, content) => {
    const elementId = `ai-${contentType}-${Date.now()}`;

    const baseElement = {
      id: elementId,
      type: contentType,
      position: { x: 50, y: 50 },
      size: { width: 400, height: 300 },
      aiGenerated: true,
      createdAt: new Date(),
      style: {
        backgroundColor: '#ffffff',
        borderRadius: 12,
        padding: 24,
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }
    };

    switch (contentType) {
      case 'press-release':
        return {
          ...baseElement,
          content: {
            headline: content?.headline || 'Generated Press Release',
            subheadline: content?.subheadline || '',
            body: content?.body || '',
            boilerplate: content?.boilerplate || '',
            contactInfo: content?.contact_info || '',
            keywords: content?.seo_keywords || []
          }
        };

      case 'author-bio':
        return {
          ...baseElement,
          type: 'author-bio',
          content: {
            name: pressKitData?.authorName || 'Author Name',
            bio: content?.body_sections?.[0]?.content || content?.introduction || '',
            image: "https://images.unsplash.com/photo-1628595556262-4cffd053a4bf",
            imageAlt: 'Professional headshot of the author in modern office setting',
            credentials: content?.title || '',
            socialLinks: []
          }
        };

      case 'social-media':
        return {
          ...baseElement,
          type: 'social-media-feed',
          content: {
            platform: 'twitter',
            posts: [{
              id: 1,
              text: content?.main_post || '',
              hashtags: content?.hashtags || [],
              engagement: { likes: 45, retweets: 12, comments: 8 }
            }],
            callToAction: content?.call_to_action || ''
          }
        };

      default:
        return {
          ...baseElement,
          content: { text: JSON.stringify(content, null, 2) }
        };
    }
  };

  // Template categories with industry-specific options
  const templateCategories = [
  {
    id: 'literary-fiction',
    name: 'Literary Fiction',
    description: 'Elegant templates for literary works',
    templates: [
    { id: 'literary-classic', name: 'Classic Literary', preview: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570' },
    { id: 'literary-modern', name: 'Modern Literary', preview: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f' },
    { id: 'literary-minimalist', name: 'Minimalist Literary', preview: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d' }]

  },
  {
    id: 'sci-fi-fantasy',
    name: 'Sci-Fi & Fantasy',
    description: 'Dynamic templates for speculative fiction',
    templates: [
    { id: 'sci-fi-futuristic', name: 'Futuristic Sci-Fi', preview: 'https://images.unsplash.com/photo-1446776653964-20c1d3a81b06' },
    { id: 'fantasy-epic', name: 'Epic Fantasy', preview: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96' },
    { id: 'cyberpunk', name: 'Cyberpunk', preview: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176' }]

  },
  {
    id: 'non-fiction',
    name: 'Non-Fiction',
    description: 'Professional templates for educational content',
    templates: [
    { id: 'business-professional', name: 'Business Professional', preview: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40' },
    { id: 'academic-research', name: 'Academic Research', preview: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570' },
    { id: 'self-help-modern', name: 'Self-Help Modern', preview: 'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07' }]

  }];


  // Navigation tabs for enhanced functionality
  const navigationTabs = [
  { id: 'design', label: 'Design Studio', icon: 'Palette' },
  { id: 'ai-content', label: 'AI Content', icon: 'Sparkles' },
  { id: 'templates', label: 'Templates', icon: 'Layout' },
  { id: 'interactive', label: 'Interactive Elements', icon: 'MousePointer' },
  { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
  { id: 'collaboration', label: 'Collaborate', icon: 'Users' },
  { id: 'export', label: 'Export & Publish', icon: 'Download' }];


  // Handle template selection with AI enhancement
  const handleTemplateSelect = async (template) => {
    setCurrentTemplate(template);

    // Auto-generate content suggestions for the selected template
    if (template?.type === 'ai-enhanced') {
      await handleAIContentGeneration('press-release', {
        templateStyle: template?.id,
        genre: pressKitData?.genre
      });
    }
  };

  // Real-time collaboration features
  const handleCollaboratorAction = (action, data) => {
    switch (action) {
      case 'add-comment':
        setCollaborators((prev) => [...prev, {
          id: Date.now(),
          type: 'comment',
          user: data?.user,
          content: data?.comment,
          elementId: data?.elementId,
          timestamp: new Date()
        }]);
        break;
      case 'suggest-edit':
        // Handle edit suggestions
        break;
      case 'approve-change':
        // Handle change approvals
        break;
    }
  };

  // Export and publishing options
  const handleExport = async (format, options = {}) => {
    try {
      switch (format) {
        case 'pdf':
          // Generate PDF with all elements
          console.log('Exporting as PDF...');
          break;
        case 'web':
          // Generate responsive web version
          console.log('Generating web version...');
          break;
        case 'wordpress':
          // Export for WordPress integration
          console.log('Exporting for WordPress...');
          break;
        case 'custom-domain':
          // Publish to custom domain
          console.log('Publishing to custom domain...');
          break;
      }
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Helmet>
        <title>Enhanced Press Kit Builder - PenGame PR Hub</title>
        <meta name="description" content="Create professional press kits with AI-powered content generation, advanced templates, and interactive multimedia elements." />
      </Helmet>
      <Header />
      <main className="pt-16">
        {/* Enhanced Header with Template Selection */}
        <div className="bg-white border-b border-slate-200 shadow-sm">
          <div className="px-6 py-4">
            <Breadcrumb />
            
            <div className="flex items-center justify-between mt-4">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 mb-1">Enhanced Press Kit Builder</h1>
                <p className="text-slate-600 text-sm">
                  AI-powered press kit creation with professional templates and interactive elements
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                {/* Version Control */}
                <div className="flex items-center space-x-2 px-3 py-1 bg-slate-100 rounded-lg">
                  <Icon name="GitBranch" size={14} className="text-slate-500" />
                  <span className="text-xs font-medium text-slate-700">v{version}</span>
                </div>
                
                {/* Collaboration Status */}
                <div className="flex items-center space-x-2 px-3 py-1 bg-emerald-50 rounded-lg">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  <span className="text-xs font-medium text-emerald-700">
                    {collaborators?.length} collaborators
                  </span>
                </div>
                
                {/* Auto-save Status */}
                <div className="flex items-center space-x-2 px-3 py-1 bg-blue-50 rounded-lg">
                  <Icon name="CloudCheck" size={14} className="text-blue-500" />
                  <span className="text-xs font-medium text-blue-700">Auto-saved</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Enhanced Navigation Tabs */}
          <div className="px-6">
            <nav className="flex space-x-6 -mb-px">
              {navigationTabs?.map((tab) =>
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab?.id ?
                'border-indigo-500 text-indigo-600 bg-indigo-50' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`
                }>

                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                  {tab?.id === 'ai-content' && isGenerating &&
                <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse" />
                }
                </button>
              )}
            </nav>
          </div>
        </div>

        {/* Press Kit Information Panel */}
        <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-slate-900 mb-1">
                    {pressKitData?.title}
                  </h2>
                  <p className="text-sm text-slate-600 mb-2">
                    {pressKitData?.subtitle}
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-slate-500">
                    <span className="flex items-center space-x-1">
                      <Icon name="Calendar" size={12} />
                      <span>Release: {pressKitData?.publishDate}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Building" size={12} />
                      <span>{pressKitData?.publisher}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Tag" size={12} />
                      <span>{pressKitData?.genre}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="BarChart3" size={12} />
                      <span>{analytics?.views} views</span>
                    </span>
                  </div>
                </div>
                
                {/* Key Themes Tags */}
                <div className="flex flex-wrap gap-1">
                  {pressKitData?.keyThemes?.map((theme, index) =>
                  <span
                    key={index}
                    className="px-2 py-1 bg-white/60 text-xs font-medium text-slate-700 rounded-md border border-white/20">

                      {theme}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                <Icon name="Settings" size={16} className="text-slate-600" />
              </button>
              <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                <Icon name="Share" size={16} className="text-slate-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area with Dynamic Panels */}
        <div className="flex h-[calc(100vh-280px)]">
          {/* Template Gallery Panel */}
          {activeTab === 'templates' &&
          <TemplateGallery
            categories={templateCategories}
            onTemplateSelect={handleTemplateSelect}
            currentTemplate={currentTemplate} />

          }
          
          {/* AI Content Generation Panel */}
          {activeTab === 'ai-content' &&
          <AIContentPanel
            onGenerateContent={handleAIContentGeneration}
            generatedContent={aiGeneratedContent}
            isGenerating={isGenerating}
            pressKitData={pressKitData} />

          }
          
          {/* Analytics Dashboard Panel */}
          {activeTab === 'analytics' &&
          <AnalyticsDashboard
            analytics={analytics}
            pressKitElements={pressKitElements} />

          }
          
          {/* Collaboration Panel */}
          {activeTab === 'collaboration' &&
          <CollaborationTools
            collaborators={collaborators}
            onCollaboratorAction={handleCollaboratorAction} />

          }
          
          {/* Export Options Panel */}
          {activeTab === 'export' &&
          <ExportOptions
            onExport={handleExport}
            pressKitData={pressKitData}
            elements={pressKitElements} />

          }

          {/* Advanced Canvas Workspace */}
          <AdvancedCanvas
            elements={pressKitElements}
            onElementUpdate={setPressKitElements}
            template={currentTemplate}
            aiContent={aiGeneratedContent}
            isVisible={['design', 'ai-content']?.includes(activeTab)} />

          
          {/* Interactive Preview Panel */}
          <InteractivePreview
            elements={pressKitElements}
            pressKitData={pressKitData}
            analytics={analytics}
            onAnalyticsUpdate={setAnalytics} />

        </div>

        {/* Enhanced Status Bar */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-200">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <div className="flex items-center space-x-6">
              <span className="flex items-center space-x-2">
                <Icon name="Layers" size={12} />
                <span>{pressKitElements?.length} elements</span>
              </span>
              <span className="flex items-center space-x-2">
                <Icon name="Sparkles" size={12} />
                <span>{Object?.keys(aiGeneratedContent)?.length} AI generated</span>
              </span>
              <span className="flex items-center space-x-2">
                <Icon name="Eye" size={12} />
                <span>SEO Score: 92/100</span>
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              <span>Mobile responsive: ✓</span>
              <span>Accessibility: AAA</span>
              <span>Last saved: {pressKitData?.lastModified?.toLocaleTimeString()}</span>
            </div>
          </div>
        </div>
      </main>
    </div>);

};

export default EnhancedPressKitBuilder;